import React, { useState } from "react";
import { Form, FormSpy, useField } from "react-final-form";
import { Alert, Box, Button, FormLabel, Tooltip } from "@mui/material";
import { FormApi } from "final-form";

import { LoadingButton, Palette } from "ui";

import { useLazyGetLayerClassificationQuery } from "fond/api";
import { SelectField, SwitchField } from "fond/form/fields";
import { NumericField } from "fond/form/fields/NumericField";
import { Attribute, ColorGradient, LayerClassification } from "fond/types";
import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { makeUuid } from "fond/utils";
import { generateColors } from "fond/utils/colors";
import { required } from "fond/utils/validation";

import ClassifyData from "./ClassifyData";
import { getFondStyleTypeFromGeometry } from "./helper";
import StyleSettingsModal from "./StyleSettingsModal";
import { useClearClassificationsOnAttributeChange } from "./useClearClassificationsOnAttributeChange";

import { Adornment } from "../LayerAttributes/layerAttributes.styles";
import { DataTableContainer } from "./Classify.styles";

export interface IClassifyFormData {
  attributeId?: string;
  colorRamp?: ColorGradient;
  invertColorRamp?: boolean;
  gradientColors?: boolean;
  range?: "equal_intervals" | "quantiles";
  groupSize?: number;
  layerClassifications: LayerClassification[];
}

interface IProps {
  colors: string[];
  initialValues: Partial<IClassifyFormData>;
  isSubmitting?: boolean;
  layerConfig: LayerConfig;
  onChange(values: IClassifyFormData): void;
}

// Inner component to access form context
const FormContent: React.FC<{
  colors: string[];
  isSubmitting: boolean;
  layerConfig: LayerConfig;
  handleSubmit: () => void;
}> = ({ colors, isSubmitting, layerConfig, handleSubmit }) => {
  useClearClassificationsOnAttributeChange();
  const [showSettings, setShowSettings] = useState(false);
  const [getClassification, { isFetching, isLoading }] = useLazyGetLayerClassificationQuery();
  const [error, setError] = useState<string | null>(null);
  const fondStyleType = getFondStyleTypeFromGeometry(layerConfig.GeometryType);
  const {
    input: { onChange: setLayerClassifications, value: layerClassifications },
  } = useField<LayerClassification[]>("layerClassifications");

  const reset = () => {
    setError(null);
  };

  /**
   * Requests the layer classification data from the backend &
   * clears previous classifications.
   */
  const handleOnClassifyClick = ({ attributeId, method, groups }: { attributeId: string; method?: string; groups?: number }) => {
    // On classification we remove past classifications & any selected row information.
    reset();

    // Request to classification information via the API.
    getClassification({ layerConfigId: layerConfig.ID, attributeConfigId: attributeId, method, groups })
      .unwrap()
      .then((response) => {
        if (response) {
          // We use structuredClone here to ensure the data is properly cloned before being set in state.
          // preventing potential issues with mutable data structures (row editing is enabled).
          setLayerClassifications(
            structuredClone([
              ...response.Classifications.map((item) => ({
                ...item,
                GeometryType: layerConfig.GeometryType,
              })),
              {
                Label: "Other",
                FilterConfiguration: {
                  Type: "other",
                },
                GeometryType: layerConfig.GeometryType,
              },
            ]).map((item) => ({ ...item, ID: makeUuid() }))
          );
        }
      })
      .catch((e) => {
        setError(e?.data?.message || e?.message || "An error occurred");
      });
  };

  const getAdornment = (type: Attribute["Type"]) => {
    if (type === "INTEGER") return <Adornment>123</Adornment>;
    if (type === "REAL") return <Adornment>1.50</Adornment>;
    return <Adornment>abc</Adornment>;
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <Box>
        <Box>
          <SelectField
            data-testid="attribute-options"
            name="attributeId"
            label="Attribute"
            displayEmpty
            placeholder="Select"
            options={
              layerConfig.Attributes?.map((attribute) => ({
                startAdornment: getAdornment(attribute.Type),
                value: attribute.ID,
                displayValue: attribute.Name,
              })) || []
            }
            size="small"
            fullWidth
            disabled={isSubmitting}
            validate={required}
          />
        </Box>
        <FormSpy subscription={{ values: true }}>
          {({ values }) => (
            <>
              <Box sx={{ mt: 2 }}>
                <SelectField
                  name="colorRamp"
                  label="Color ramp"
                  displayEmpty
                  placeholder="Select"
                  options={[
                    {
                      value: "",
                      displayValue: "Random colors",
                    },
                    ...Object.values(ColorGradient).map((variant) => ({
                      value: variant,
                      displayValue: variant,
                      startAdornment: <Palette colors={generateColors(variant, values?.invertColorRamp, 5)} sx={{ mr: 1 }} />,
                    })),
                  ]}
                  disabled={isSubmitting}
                  size="small"
                  fullWidth
                />
              </Box>
              {values?.colorRamp && (
                <Box sx={{ mt: 2 }}>
                  <SwitchField
                    name="invertColorRamp"
                    color="primary"
                    label="Invert color ramp"
                    data-testid="invert-color-ramp-switch"
                    fieldProps={{ type: "checkbox" }}
                  />
                  <Tooltip title="The color of each feature will be determined by the value of the attribute selected & the min / max value defined.">
                    <Box display="inline-block">
                      <SwitchField
                        name="gradientColors"
                        color="primary"
                        label="Gradient colors"
                        data-testid="gradient-colors-switch"
                        fieldProps={{ type: "checkbox" }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
              )}
            </>
          )}
        </FormSpy>
        <FormSpy subscription={{ values: true }}>
          {({ values }) => {
            const attribute = layerConfig.Attributes?.find(({ ID }) => ID === values.attributeId);
            if (!attribute || attribute.Type === "STRING") return null;

            return (
              <Box display="flex" sx={{ mt: 2, gap: 1 }}>
                <SelectField
                  name="range"
                  label="Range"
                  displayEmpty
                  placeholder="Select"
                  options={[
                    {
                      value: "quantiles",
                      displayValue: "Equal count (quantile)",
                    },
                    {
                      value: "equal_intervals",
                      displayValue: "Equal interval",
                    },
                  ]}
                  size="small"
                  fullWidth
                  disabled={isSubmitting}
                />
                <NumericField
                  data-testid="group-size-input"
                  name="groupSize"
                  label="No. of groups"
                  fullWidth
                  type="number"
                  validate={required}
                  disabled={isSubmitting}
                />
              </Box>
            );
          }}
        </FormSpy>
      </Box>
      {error && (
        <Alert severity="warning" sx={{ my: 1 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ my: 1, display: "flex", justifyContent: "flex-end" }}>
        <FormSpy subscription={{ values: true, invalid: true }}>
          {({ invalid, values }) => (
            <LoadingButton
              data-testid="generate-classification-button"
              color="primary"
              type="submit"
              size="small"
              disabled={invalid || isSubmitting}
              loading={isFetching || isLoading}
              onClick={() => {
                const attribute = layerConfig.Attributes?.find(({ ID }) => ID === values.attributeId);
                if (attribute) {
                  const method = attribute.Type === "STRING" ? "distinct_values" : values?.range;
                  const groups = attribute.Type !== "STRING" ? values?.groupSize : undefined;
                  handleOnClassifyClick({ attributeId: values.attributeId, method, groups });
                }
              }}
            >
              Generate classifications
            </LoadingButton>
          )}
        </FormSpy>
        {showSettings && <StyleSettingsModal type={fondStyleType} onClose={() => setShowSettings(false)} />}
      </Box>
      <FormSpy
        subscription={{ values: true, valid: true, pristine: true }}
        onChange={({ valid, pristine }) => {
          if (valid && !pristine) handleSubmit();
        }}
      />
      <Box mt={4} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <FormLabel>Layers</FormLabel>
        {layerClassifications && layerClassifications.length > 0 && (
          <Button data-testid="style-settings-button" onClick={() => setShowSettings(true)} variant="outlined" size="small">
            Style settings
          </Button>
        )}
      </Box>
      <DataTableContainer>
        <FormSpy subscription={{ values: true }}>
          {({ values }) => {
            const attribute = layerConfig.Attributes?.find(({ ID }) => ID === values.attributeId);
            return (
              <ClassifyData colors={colors} attributeId={attribute?.Name} attributeType={attribute?.Type} gradientColors={!!values.gradientColors} />
            );
          }}
        </FormSpy>
      </DataTableContainer>
    </form>
  );
};

const ClassifyForm: React.FC<IProps> = ({ colors, initialValues, isSubmitting = false, layerConfig, onChange }: IProps) => {
  const onFormUpdate = (values: IClassifyFormData, _: FormApi<IClassifyFormData>) => {
    onChange(values);
  };

  return (
    <Form<IClassifyFormData>
      initialValues={initialValues}
      onSubmit={onFormUpdate}
      render={({ handleSubmit }) => <FormContent colors={colors} isSubmitting={isSubmitting} layerConfig={layerConfig} handleSubmit={handleSubmit} />}
    />
  );
};

export default ClassifyForm;
